import React, { useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { CENTRAL_PB_URL, ENVIRONMENTS } from '../../config/constants';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';

const {
  FiBox,
  FiCode,
  FiCopy,
  FiDownload,
  FiCheckCircle,
  FiChevronRight,
  FiSettings,
  FiLink,
  FiShield,
  FiRefreshCw,
} = FiIcons;

const DEFAULTS = {
  supportPbUrl: CENTRAL_PB_URL,
  appId: '',
  appName: '',
  environment: 'Prod',
  appVersion: '1.0.0',
  supportRoute: '/support',
  identityMode: 'fromAppAuth',
  tenantMode: 'none',
  tenantKey: 'tenantId',
};

const HelpCenter = ({ onViewChange }) => {
  const { apps, showToast } = useApp();
  const { isAdmin } = useAuth();
  const activeApps = apps.filter((app) => app.isActive !== false);
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [variables, setVariables] = useState(DEFAULTS);
  const [expandedFaq, setExpandedFaq] = useState('tenant');

  const selectApp = (recordId) => {
    setSelectedRecordId(recordId);
    const app = activeApps.find((item) => item.id === recordId);
    if (!app) {
      setVariables(DEFAULTS);
      return;
    }
    setVariables((current) => ({
      ...current,
      appId: app.appId || '',
      appName: app.appName || '',
    }));
  };

  const setVar = (key, value) => setVariables((current) => ({ ...current, [key]: value }));
  const prompt = useMemo(() => buildCopilotPrompt(variables), [variables]);
  const ready = Boolean(variables.appId.trim() && variables.appName.trim());

  const copyPrompt = async () => {
    if (!ready) return;
    try {
      await navigator.clipboard.writeText(prompt);
      showToast('GitHub Copilot prompt copied');
    } catch {
      showToast('Could not copy prompt', 'error');
    }
  };

  const downloadPrompt = () => {
    if (!ready) return;
    const blob = new Blob([prompt], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${variables.appId || 'support-integration'}-github-copilot-prompt.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    showToast('Prompt downloaded');
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-primary mb-1">Guides and integration</p>
          <h2 className="text-3xl font-bold text-text">Help Center</h2>
          <p className="text-sm text-muted mt-1 max-w-3xl">
            Create a new client app record, then generate a ready-to-run GitHub Copilot prompt that connects that app back to BPA Solutions Support Hub.
          </p>
        </div>
        {isAdmin && (
          <button type="button" onClick={() => onViewChange?.('admin')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border bg-surface text-text hover:bg-background font-semibold shadow-sm">
            <SafeIcon icon={FiSettings} className="w-4 h-4 text-primary" />
            Open Apps Admin
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <FaqCard icon={FiBox} title="How do I create a new tenant or client app?" open={expandedFaq === 'tenant'} onToggle={() => setExpandedFaq(expandedFaq === 'tenant' ? '' : 'tenant')}>
          <ol className="space-y-3 text-sm text-text">
            <Step number="1" title="Open Admin, then Apps">Only an Admin can register a new client application in Support Hub.</Step>
            <Step number="2" title="Choose Create App">Enter a friendly App Name and a stable App ID such as storage_service_business_hub_app.</Step>
            <Step number="3" title="Keep the App ID stable">Use lowercase letters, numbers and underscores. Do not rename the App ID after client integrations start using it.</Step>
            <Step number="4" title="Leave the app Active">The client app must be active so its support page can resolve the correct Support Hub relation record.</Step>
            <Step number="5" title="Return to this Help Center">Select the new app below and the Copilot prompt updates automatically.</Step>
          </ol>
          <div className="mt-4 rounded-xl bg-accent-soft border border-border p-3 text-sm">
            <strong>Terminology:</strong> Support Hub stores each integrated client product as an <strong>App</strong>. If you refer to a client deployment as a tenant, create its corresponding record under Admin &gt; Apps.
          </div>
        </FaqCard>

        <FaqCard icon={FiCode} title="How does the integration prompt get generated?" open={expandedFaq === 'prompt'} onToggle={() => setExpandedFaq(expandedFaq === 'prompt' ? '' : 'prompt')}>
          <ol className="space-y-3 text-sm text-text">
            <Step number="1" title="Select an existing app">The builder reads the App Name and App ID already registered in Support Hub.</Step>
            <Step number="2" title="Review the variables">Environment, version, support route, identity source and optional tenant source are all editable.</Step>
            <Step number="3" title="Copy the full prompt">Paste it into GitHub Copilot inside the target client application's repository.</Step>
            <Step number="4" title="Copilot adds only the support integration">The generated instructions protect the client's existing backend, auth, business logic and routes.</Step>
            <Step number="5" title="Run the acceptance test">Submit one test ticket and confirm that its real BPAS reference appears in this Support Hub.</Step>
          </ol>
        </FaqCard>
      </div>

      <section className="bg-surface rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-primary text-sm font-semibold mb-1">
              <SafeIcon icon={FiLink} className="w-4 h-4" />
              Client app integration
            </div>
            <h3 className="text-xl font-bold text-text">GitHub Copilot Prompt Builder</h3>
            <p className="text-sm text-muted mt-1">Select an app and every variable is injected into the full integration prompt in real time.</p>
          </div>
          <div className="inline-flex items-center gap-2 text-xs font-semibold rounded-full bg-primary-soft text-primary px-3 py-1.5 border border-border">
            <SafeIcon icon={FiShield} className="w-3.5 h-3.5" />
            No secrets included
          </div>
        </div>

        <div className="p-6 grid xl:grid-cols-[minmax(360px,0.8fr)_minmax(0,1.2fr)] gap-6">
          <div className="space-y-5">
            <Field label="Select registered app" hint="Choose an active app already created in Support Hub.">
              <select value={selectedRecordId} onChange={(event) => selectApp(event.target.value)} className="control">
                <option value="">Select an app...</option>
                {activeApps.map((app) => <option key={app.id} value={app.id}>{app.appName}</option>)}
              </select>
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="App Name" hint="Human-readable name used in the prompt.">
                <input value={variables.appName} onChange={(event) => setVar('appName', event.target.value)} className="control" placeholder="Storage Service Business Hub" />
              </Field>
              <Field label="App ID" hint="Stable machine identifier registered in Support Hub.">
                <input value={variables.appId} onChange={(event) => setVar('appId', normalizeAppId(event.target.value))} className="control font-mono" placeholder="storage_service_business_hub_app" />
              </Field>
            </div>

            <Field label="Support Hub PocketBase URL" hint="Normally leave this unchanged.">
              <input value={variables.supportPbUrl} onChange={(event) => setVar('supportPbUrl', event.target.value)} className="control font-mono text-sm" />
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Environment">
                <select value={variables.environment} onChange={(event) => setVar('environment', event.target.value)} className="control">
                  {ENVIRONMENTS.map((value) => <option key={value}>{value}</option>)}
                </select>
              </Field>
              <Field label="App Version">
                <input value={variables.appVersion} onChange={(event) => setVar('appVersion', event.target.value)} className="control" placeholder="1.0.0" />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Support Route">
                <input value={variables.supportRoute} onChange={(event) => setVar('supportRoute', event.target.value)} className="control font-mono" placeholder="/support" />
              </Field>
              <Field label="Identity Mode">
                <select value={variables.identityMode} onChange={(event) => setVar('identityMode', event.target.value)} className="control">
                  <option value="fromAppAuth">fromAppAuth</option>
                  <option value="manualEntry">manualEntry</option>
                </select>
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Tenant ID Mode" hint="Use none unless the client app genuinely needs tenant reporting.">
                <select value={variables.tenantMode} onChange={(event) => setVar('tenantMode', event.target.value)} className="control">
                  <option value="none">none</option>
                  <option value="fromUser">fromUser</option>
                  <option value="fromLocalStorage">fromLocalStorage</option>
                  <option value="fromUrlParam">fromUrlParam</option>
                </select>
              </Field>
              <Field label="Tenant ID Key">
                <input value={variables.tenantKey} onChange={(event) => setVar('tenantKey', event.target.value)} disabled={variables.tenantMode === 'none'} className="control font-mono disabled:opacity-50" />
              </Field>
            </div>

            <div className="rounded-xl border border-border bg-background p-4 text-sm">
              <div className="flex items-start gap-3">
                <SafeIcon icon={ready ? FiCheckCircle : FiRefreshCw} className={`w-5 h-5 mt-0.5 ${ready ? 'text-accent' : 'text-muted'}`} />
                <div>
                  <p className="font-semibold text-text">{ready ? 'Prompt ready' : 'Select or enter an App Name and App ID'}</p>
                  <p className="text-muted mt-1">The prompt updates automatically as you change any variable.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div>
                <p className="font-semibold text-text">Generated GitHub Copilot prompt</p>
                <p className="text-xs text-muted mt-0.5">Paste the entire prompt into Copilot in the target app repository.</p>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={copyPrompt} disabled={!ready} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-border bg-surface hover:bg-background font-semibold text-sm disabled:opacity-40">
                  <SafeIcon icon={FiCopy} className="w-4 h-4 text-primary" />Copy Prompt
                </button>
                <button type="button" onClick={downloadPrompt} disabled={!ready} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-primary text-surface hover:bg-primary-hover font-semibold text-sm disabled:opacity-40">
                  <SafeIcon icon={FiDownload} className="w-4 h-4" />Download
                </button>
              </div>
            </div>
            <pre className="min-h-[680px] max-h-[760px] overflow-auto whitespace-pre-wrap break-words rounded-2xl bg-text text-surface p-5 text-xs leading-6 border border-border font-mono shadow-inner">
              {ready ? prompt : 'Select an app above to generate the complete integration prompt.'}
            </pre>
          </div>
        </div>
      </section>
    </div>
  );
};

const FaqCard = ({ icon, title, open, onToggle, children }) => (
  <section className="bg-surface rounded-2xl border border-border shadow-sm overflow-hidden">
    <button type="button" onClick={onToggle} className="w-full flex items-center gap-4 p-5 text-left hover:bg-background">
      <div className="w-11 h-11 rounded-xl bg-primary-soft flex items-center justify-center shrink-0">
        <SafeIcon icon={icon} className="w-5 h-5 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted">FAQ</p>
        <h3 className="font-bold text-text mt-0.5">{title}</h3>
      </div>
      <SafeIcon icon={FiChevronRight} className={`w-5 h-5 text-muted transition-transform ${open ? 'rotate-90' : ''}`} />
    </button>
    {open && <div className="px-5 pb-5 pt-1 border-t border-border">{children}</div>}
  </section>
);

const Step = ({ number, title, children }) => (
  <li className="flex gap-3">
    <span className="w-7 h-7 rounded-full bg-primary text-surface flex items-center justify-center text-xs font-bold shrink-0">{number}</span>
    <div><p className="font-semibold">{title}</p><p className="text-muted mt-0.5">{children}</p></div>
  </li>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-text mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-xs text-muted mt-1.5">{hint}</span>}
  </label>
);

const normalizeAppId = (value) => value
  .toLowerCase()
  .replace(/[\s-]+/g, '_')
  .replace(/[^a-z0-9_]/g, '')
  .replace(/_+/g, '_')
  .replace(/^_+/, '');

const buildCopilotPrompt = (v) => `GITHUB COPILOT TASK: ADD BPA SOLUTIONS IN-APP SUPPORT INTEGRATION

Implement a tightly scoped in-app Support page in the EXISTING ${v.appName} application.

DO NOT rebuild or broadly refactor the application.
DO NOT change existing authentication, business logic, calculations, routes, database architecture or primary backend configuration except for adding the Support route/navigation required below.
DO NOT add Netlify Functions.
DO NOT add Google Apps Script.
DO NOT put any Support Hub admin credentials or secret API keys in the frontend.
DO NOT add unnecessary dependencies.
Reuse the application's existing components, design system, responsive layout and color palette.

CONFIGURATION VARIABLES
SUPPORT_PB_URL = "${v.supportPbUrl}"
SUPPORT_APP_ID = "${v.appId}"
SUPPORT_APP_NAME = "${v.appName}"
SUPPORT_ENVIRONMENT = "${v.environment}"
SUPPORT_APP_VERSION = "${v.appVersion}"
SUPPORT_PAGE_ROUTE = "${v.supportRoute}"
IDENTITY_MODE = "${v.identityMode}"
SUPPORT_TENANT_ID_MODE = "${v.tenantMode}"
SUPPORT_TENANT_ID_KEY = "${v.tenantKey}"

GOAL
Add a native Support page that allows an end user to submit a support ticket directly to BPA Solutions Support Hub. The client app must keep using its existing backend for normal application data. This support integration is a separate direct HTTP connection to SUPPORT_PB_URL only for Support Hub operations.

1. NAVIGATION AND PAGE
Add a Support navigation item using the application's existing navigation pattern. Route it to SUPPORT_PAGE_ROUTE. The page must visually match the existing application and reuse existing Button, Input, Select, Textarea, Card, Alert and Toast patterns wherever available.

2. FORM FIELDS
Required:
- Ticket Type with exactly: Bug Report, Feature Request, Billing, How To, Other
- Subject
- Description

Optional:
- Severity with exactly: Low, Medium, High, Critical
- Screenshots, maximum 5 images

3. REQUESTER IDENTITY
IDENTITY_MODE = "${v.identityMode}".
If fromAppAuth, read requesterName and requesterEmail from the application's existing authenticated user/session without changing auth. If either value is unavailable, show manual Name and Email fields and require them before submit.
If manualEntry, always show required Name and Email fields.

4. OPTIONAL TENANT CONTEXT
SUPPORT_TENANT_ID_MODE = "${v.tenantMode}".
If mode is none, do not send tenantId.
If mode is fromUser, read SUPPORT_TENANT_ID_KEY from the existing authenticated user object when available.
If mode is fromLocalStorage, read SUPPORT_TENANT_ID_KEY from localStorage.
If mode is fromUrlParam, read SUPPORT_TENANT_ID_KEY from the current URL query string.
Never fail ticket submission solely because an optional tenantId is unavailable.

5. RESOLVE THE SUPPORT HUB APP RECORD
Do NOT put SUPPORT_APP_ID directly into the ticket app relation field.
Before ticket submission, query the central PocketBase apps collection and find the single active record where appId exactly equals SUPPORT_APP_ID and isActive = true.
Cache the returned PocketBase apps record ID for the current session/page instance.
If no active record is found, show: "Support is not configured for this app. Please contact support."
Do not expose or list other Support Hub applications beyond this lookup.

6. GENERATE THE REAL TICKET REFERENCE CLIENT-SIDE BEFORE POST
Implement generateTicketRef().
Format: BPAS-YYYYMMDD-HHMMSS-XXXX
XXXX = 4 random uppercase alphanumeric characters.
Generate and store the reference BEFORE the ticket POST. Include the exact value in the ticket payload. The success screen must use this stored reference and must not depend on the server returning ticketRef. Do not use REF-PENDING during a normal successful flow.

7. CAPTURE SUPPORT METADATA
Automatically capture:
- pageUrl = window.location.href
- environment = SUPPORT_ENVIRONMENT
- appVersion = SUPPORT_APP_VERSION
- clientMeta JSON with userAgent, platform, language, timezone, screenWidth and screenHeight

8. CREATE THE TICKET
Use FormData so screenshots can be attached.
POST to:
SUPPORT_PB_URL + "/api/collections/tickets/records"

FormData fields:
- app = resolved Support Hub apps PocketBase record ID
- ticketRef = generated ticket reference
- type
- subject
- description
- status = "New"
- severity when supplied
- requesterName
- requesterEmail
- tenantId only when available and mode is not none
- pageUrl
- appVersion
- environment
- clientMeta = JSON string
- source = "App"
- append every screenshot using field name screenshots

Maximum screenshots = 5. If the user selects more than 5, block submission and show a clear validation message.

9. CREATE THE INITIAL TICKET LOG AFTER TICKET SUCCESS
After the ticket create request succeeds and a created ticket record ID is available, POST JSON to:
SUPPORT_PB_URL + "/api/collections/ticket_logs/records"

Payload:
- ticket = created ticket ID
- authorType = "EndUser"
- authorName = requesterName
- authorEmail = requesterEmail
- message = "Ticket submitted from ${v.appName} in app support"
- isInternal = false

Ticket log creation is NON-BLOCKING. If the ticket was created but the log fails, keep the overall submission successful and only show a small warning if useful.

10. SUCCESS EXPERIENCE
Show a polished success state containing:
- "Your support request has been submitted successfully."
- the real created ticket reference
- Copy Ticket Reference button
- Submit Another Ticket button

Submit Another Ticket resets the form, screenshots, created ticket ID and ticket reference while retaining safe auto-derived identity values.

11. ERROR HANDLING
- Disable Submit while submitting to prevent duplicates.
- Parse PocketBase JSON errors and show the useful validation message.
- Keep user-entered form values after a failed submit.
- App lookup failure must show the configuration error instead of a broken form.
- A failed initial ticket log must not make a successfully created ticket appear failed.

12. SECURITY AND ISOLATION
- Do not replace the application's existing PocketBase/Supabase/other backend client.
- Do not reuse the client application's authentication token against Support Hub.
- Do not add Support Hub admin functions.
- Do not list Support Hub tickets in the client app.
- Do not expose secrets.
- Do not change Support Hub PocketBase schema or API rules from this client application.

13. BUILD SAFETY
- Preserve the existing build system and dependencies.
- Do not make linting a prerequisite for the production preview/build unless the repository already requires it.
- Do not add server-only files to frontend lint/build scope.
- Keep all new code readable, maintainable and non-minified.

ACCEPTANCE TESTS
1. Support appears in the existing navigation.
2. ${v.supportRoute} opens successfully.
3. The page matches ${v.appName}'s existing UI.
4. The active Support Hub app record resolves using ${v.appId}.
5. Required field validation works.
6. More than 5 screenshots is blocked.
7. A test ticket is created in BPA Solutions Support Hub.
8. The ticket appears under ${v.appName}.
9. A real BPAS ticket reference is shown, not REF-PENDING.
10. Copy Ticket Reference works.
11. Initial ticket log is attempted and is non-blocking.
12. Existing app auth, backend, calculations and unrelated pages remain unchanged.

MANDATORY END-OF-RUN REPORT
Report:
- exact files changed
- exact files added
- configuration values used
- requester identity mapping used
- tenant mapping used
- tests performed and results
- confirmation that no Netlify Function or Google Apps Script was added
- confirmation that no primary backend/auth/business logic was changed
- unresolved items, or "None"

Implement only this support integration now.`;

export default HelpCenter;